// Compress / re-encode user photos before upload (Plan v2 G6).
//
// Goals:
//   - longest edge ≤ maxDimension (default 1280) — keeps blob under ~1MB which
//     base64-expands to ~1.35MB, well under the API's 1.65MB dataUrl ceiling.
//   - WebP by default — ~30% smaller than JPEG at equal quality.
//   - Defensive EXIF strip: re-rendering through canvas drops all metadata
//     (orientation, GPS, camera ID) as a side-effect. Server still re-strips.

import { canvasToBlob, makeCanvas } from './canvasExport'

export type CompressOptions = {
  maxDimension?: number
  format?: 'image/webp' | 'image/jpeg'
  quality?: number
}

const DEFAULTS = {
  maxDimension: 1280,
  format: 'image/webp' as const,
  quality: 0.78,
}

const SIZE_CAP_BYTES = 1_000_000
const FALLBACK_QUALITY = 0.7

export async function compressImage(file: Blob, opts: CompressOptions = {}): Promise<Blob> {
  const maxDimension = opts.maxDimension ?? DEFAULTS.maxDimension
  const format = opts.format ?? DEFAULTS.format
  const quality = opts.quality ?? DEFAULTS.quality

  const bitmap = await createImageBitmap(file)
  try {
    const { width, height } = scaleToFit(bitmap.width, bitmap.height, maxDimension)

    const canvas = makeCanvas(width, height)
    const ctx = canvas.getContext('2d') as
      | CanvasRenderingContext2D
      | OffscreenCanvasRenderingContext2D
      | null
    if (!ctx) {
      throw new Error('compressImage: 2d context unavailable')
    }
    ctx.drawImage(bitmap, 0, 0, width, height)

    const blob = await canvasToBlob(canvas, format, quality)
    if (blob.size > SIZE_CAP_BYTES) {
      return canvasToBlob(canvas, format, FALLBACK_QUALITY)
    }
    return blob
  } finally {
    bitmap.close()
  }
}

function scaleToFit(
  srcWidth: number,
  srcHeight: number,
  maxDimension: number,
): { width: number; height: number } {
  const longest = Math.max(srcWidth, srcHeight)
  if (longest <= maxDimension) {
    return { width: srcWidth, height: srcHeight }
  }
  const ratio = maxDimension / longest
  return {
    width: Math.round(srcWidth * ratio),
    height: Math.round(srcHeight * ratio),
  }
}
